import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { lightFormat, subYears } from 'date-fns';
import { isMobile } from 'react-device-detect';

import { AccountingApi } from '_api';
import { FIELD_KEYS } from '_constants/forms';
import { MODAL_KEYS } from '_constants/modals';
import { handleError, handleResponse } from '_helpers/api';
import { sanitizeFileName, saveFile } from '_helpers/file';
import { getFileTypes } from '_store/fileTypes/slice';
import { hideModal } from '_store/modals/slice';
import { selectActiveOrderId } from '_store/order/selector';
import { selectParams as selectTasksParams } from '_store/tasks/selector';
import { getTasks, resetData as resetTaskData, resetParams as resetTasksParams } from '_store/tasks/slice';

import { selectForm, selectParams } from './selector';

export const getStatements = createAsyncThunk(
  'statements/getStatements',
  async function ({ customerId, productId }, { getState, rejectWithValue }) {
    try {
      const params = selectParams(getState());

      const response = await AccountingApi.getStatements({
        customerId,
        productId,
        ...params,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const createStatement = createAsyncThunk(
  'statements/createStatement',
  async function ({ customerId, productId }, { dispatch, getState, rejectWithValue }) {
    try {
      const form = selectForm(getState());
      const { year, startDate, endDate, description, fileType, file } = form || {};

      if (!file) {
        return;
      }

      const body = new FormData();

      if (file?.name) {
        body.append('file', file, sanitizeFileName(file.name));
      } else {
        body.append('file', file);
      }

      await AccountingApi.createStatement({
        customerId,
        productId,
        year: lightFormat(year, 'yyyy'),
        startDate: lightFormat(startDate, 'yyyy-MM-dd'),
        endDate: lightFormat(endDate, 'yyyy-MM-dd'),
        description,
        type: fileType,
        body,
      });

      dispatch(hideModal({ key: MODAL_KEYS.documentUpload }));
      dispatch(resetForm());
      dispatch(getStatements({ customerId, productId }));
      dispatch(getFileTypes({ customerId, productId }));
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const uploadStatement = createAsyncThunk(
  'statements/uploadStatement',
  async function ({ customerId, productId, statementId, file }, { getState, dispatch, rejectWithValue }) {
    try {
      if (!file) {
        return;
      }

      const body = new FormData();

      if (file?.name) {
        body.append('file', file, sanitizeFileName(file.name));
      } else {
        body.append('file', file);
      }

      await AccountingApi.uploadStatement({
        customerId,
        productId,
        statementId,
        body,
      });

      dispatch(getStatements({ customerId, productId }));
      dispatch(getFileTypes({ customerId, productId }));

      dispatch(resetTaskData());

      const orderId = selectActiveOrderId(getState());
      const tasksParams = selectTasksParams(getState());

      if (tasksParams[FIELD_KEYS.page] === 1) {
        dispatch(getTasks({ customerId, orderId }));
      } else {
        dispatch(resetTasksParams());
      }
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const downloadStatement = createAsyncThunk(
  'statements/downloadStatement',
  async function ({ customerId, productId, statementId, fileName }, { rejectWithValue }) {
    try {
      const response = await AccountingApi.downloadStatement({
        customerId,
        productId,
        statementId,
      });

      const data = handleResponse(response);
      saveFile(data, fileName);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const deleteStatement = createAsyncThunk(
  'statements/deleteStatement',
  async function ({ customerId, productId, statementId }, { dispatch, rejectWithValue }) {
    try {
      await AccountingApi.deleteStatement({
        customerId,
        productId,
        statementId,
      });

      dispatch(getStatements({ customerId, productId }));
      dispatch(getFileTypes({ customerId, productId }));
      dispatch(hideModal({ key: MODAL_KEYS.statementDelete }));
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const initialParams = {
  [FIELD_KEYS.page]: 1,
  [FIELD_KEYS.count]: isMobile ? 1 : 10,
  [FIELD_KEYS.startDateFrom]: null,
  [FIELD_KEYS.startDateTo]: null,
  [FIELD_KEYS.endDateFrom]: null,
  [FIELD_KEYS.endDateTo]: null,
  [FIELD_KEYS.description]: null,
  [FIELD_KEYS.type]: null,
  [FIELD_KEYS.year]: null,
  [FIELD_KEYS.file]: null,
};

export const initialForm = {
  [FIELD_KEYS.startDate]: subYears(new Date(), 1),
  [FIELD_KEYS.endDate]: subYears(new Date(), 1),
  [FIELD_KEYS.description]: '',
  [FIELD_KEYS.fileType]: '',
  [FIELD_KEYS.year]: subYears(new Date(), 1),
  [FIELD_KEYS.file]: null,
};

const statementsSlice = createSlice({
  name: 'statements',
  initialState: {
    form: initialForm,
    params: initialParams,
    data: {},
    error: null,
    fetchCounter: 0,
    loading: 0,
    uploading: false,
    updating: false,
    downloading: false,
    deleting: false,
  },
  reducers: {
    refreshForm(state, action) {
      state.form = {
        ...state.form,
        ...action.payload,
      };
    },
    resetForm(state) {
      state.form = {
        ...initialForm,
      };
    },
    refreshParams(state, action) {
      state.params = {
        ...state.params,
        ...action.payload,
        [FIELD_KEYS.year]: action.payload[FIELD_KEYS.year]
          ? lightFormat(action.payload[FIELD_KEYS.year], 'yyyy')
          : action.payload[FIELD_KEYS.year] === null
          ? null
          : state.params[FIELD_KEYS.year],
      };
    },
    resetData(state) {
      state.data = {};
    },
    resetError(state) {
      state.error = null;
    },
    resetFetchCounter(state) {
      state.fetchCounter = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStatements.pending, (state) => {
        state.error = null;
        state.loading += 1;
      })
      .addCase(getStatements.fulfilled, (state, action) => {
        state.data = action.payload;
        state.fetchCounter += 1;
        state.loading -= 1;
      })
      .addCase(getStatements.rejected, (state, action) => {
        state.error = action.payload;
        state.loading -= 1;
      });

    builder
      .addCase(createStatement.pending, (state) => {
        state.error = null;
        state.uploading = true;
      })
      .addCase(createStatement.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(createStatement.rejected, (state, action) => {
        state.error = action.payload;
        state.uploading = false;
      });

    builder
      .addCase(uploadStatement.pending, (state) => {
        state.error = null;
        state.updating = true;
      })
      .addCase(uploadStatement.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(uploadStatement.rejected, (state, action) => {
        state.error = action.payload;
        state.updating = false;
      });

    builder
      .addCase(downloadStatement.pending, (state) => {
        state.error = null;
        state.downloading = true;
      })
      .addCase(downloadStatement.fulfilled, (state) => {
        state.downloading = false;
      })
      .addCase(downloadStatement.rejected, (state, action) => {
        state.error = action.payload;
        state.downloading = false;
      });

    builder
      .addCase(deleteStatement.pending, (state) => {
        state.error = null;
        state.deleting = true;
      })
      .addCase(deleteStatement.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteStatement.rejected, (state, action) => {
        state.error = action.payload;
        state.deleting = false;
      });
  },
});

export const { refreshForm, resetForm, refreshParams, resetData, resetError, resetFetchCounter } =
  statementsSlice.actions;

export default statementsSlice.reducer;
